import React from 'react';
import moment from 'moment';
import TagManager from 'react-gtm-module';
import { padStart } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Button from '@material-ui/core/Button';
import Add from '@material-ui/icons/LibraryAdd';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import InfoIcon from '@material-ui/icons/Info';
import withWidth from '@material-ui/core/withWidth';
import { createStructuredSelector } from 'reselect';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { constructGTM } from '../../utilities/helpers';

import {
  JOB_STATUS_CODES,
  SORT_DIRECTIONS,
  VIEWABLE_FORM_MODES,
} from '../../constants';
import styles from './styles';
import {
  selectJobs,
  selectJobsLoading,
  selectJobsRetrieved,
} from '../../store/selectors';
import JobModal, { JOB_MODAL_ACTION } from '../JobModal';
import ActionsButton from '../ActionsButton';
import ModalController from '../ModalController';
import { openModal } from '../../store/actions/modal';
import JobStatus from '../JobStatus';
import JobsContainerMobile from './mobile/JobsContainer';
import JobsContainerDesktop from './desktop/JobsContainer';
import { getJobs, updateJob } from '../../store/actions/jobs';

const propTypes = {
  width: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']).isRequired,
  jobs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      dateCreated: PropTypes.string.isRequired,
      customerDetail: PropTypes.shape({
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
        phoneNumber: PropTypes.string,
      }),
      status: PropTypes.shape({
        code: PropTypes.oneOf([
          JOB_STATUS_CODES.STATUS_01,
          JOB_STATUS_CODES.STATUS_02,
          JOB_STATUS_CODES.STATUS_03,
          JOB_STATUS_CODES.STATUS_04,
        ]).isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      statusTransitions: PropTypes.arrayOf(
        PropTypes.shape({
          code: PropTypes.oneOf([
            JOB_STATUS_CODES.STATUS_01,
            JOB_STATUS_CODES.STATUS_02,
            JOB_STATUS_CODES.STATUS_03,
            JOB_STATUS_CODES.STATUS_04,
          ]).isRequired,
          name: PropTypes.string.isRequired,
        }).isRequired,
      ).isRequired,
      notes: PropTypes.string,
    }).isRequired,
  ).isRequired,
  onGetUserJobs: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  jobsRetrieved: PropTypes.bool.isRequired,
  classes: PropTypes.shape({
    loadingWrapper: PropTypes.string,
  }).isRequired,
  onOpenModal: PropTypes.func.isRequired,
  onUpdateJob: PropTypes.func.isRequired,
};

export const CREATE_JOB_MODAL_INDEX = 0;
export const ACTIONS_BUTTON_TITLE = 'Manage jobs';

export class MyJobsContent extends React.PureComponent {
  state = {
    sort: {
      column: 'id',
      direction: SORT_DIRECTIONS.DESC,
    },
  };

  componentDidMount() {
    const { jobsRetrieved, onGetUserJobs } = this.props;
    if (!jobsRetrieved) onGetUserJobs(localStorage.getItem('accessToken'));
  }

  handleSort = (column, direction) => {
    this.setState({
      sort: {
        column,
        direction,
      },
    });
  };

  handleJobStatusChange = (jobId, event) => {
    const { onUpdateJob } = this.props;
    const accessToken = localStorage.getItem('accessToken');

    onUpdateJob(accessToken, jobId, {
      statusCode: event.target.value,
    });
  };

  convertDateFormat = (dateSt, targetFormat) =>
    moment(dateSt).format(targetFormat);

  formatJobs = rawJobs =>
    rawJobs.map(job => ({
      ...job,
      rawId: job.id, // we need the raw id when making request to the BE as the job identifier
      customerDetail_email: job.customerDetail.email,
      /* eslint-disable prettier/prettier, react/jsx-first-prop-new-line */
      customerDetail_name: `${job.customerDetail.firstName} ${job.customerDetail.lastName}`,
      customerDetail_phone: job.customerDetail.phoneNumber,
      id: padStart(job.id,4,0),
      status: job.status.value,
      statusCode: job.status.code,
      dateCreated: this.convertDateFormat(
        job.dateCreated,
        'DD/MM/YYYY',
      ),
    }));

  getJobActions = job => {
    const viewJobAction = {
      label: 'View Job',
      action: () => {
        this.openJobModal(job, JOB_MODAL_ACTION.VIEW);
      },
    };

    const editJobAction = {
      label: 'Edit Job',
      action: () => {
        this.openJobModal(job, JOB_MODAL_ACTION.UPDATE);
      },
    };

    return [viewJobAction, editJobAction];
  };

  openJobModal = (job, jobAction) => {
    const { onOpenModal } = this.props;
    const modalIndex = jobAction === JOB_MODAL_ACTION.CREATE ? CREATE_JOB_MODAL_INDEX : job.id;
    const formMode = jobAction === JOB_MODAL_ACTION.VIEW ? VIEWABLE_FORM_MODES.VIEWING : VIEWABLE_FORM_MODES.EDITING;
    const modalProps = {
      modalIndex,
      formMode,
      jobModalAction: jobAction,
    };
    if(jobAction !== JOB_MODAL_ACTION.CREATE) {
      modalProps.jobDetails = job;
    }
    onOpenModal('styledJobModal', modalProps);
  }

  render() {
    const { sort } = this.state;
    const { width, jobs, isLoading, classes } = this.props;

    const registerEventToGtm = () => {
      // Log event to GTM
      TagManager.initialize(
        constructGTM({
          dataLayer: {
            event: 'User clicked on Create New Job',
          },
        }),
      );
    };

    const columns = [
      {
        index: 0,
        title: 'ID',
        name: 'id',
        cell: row => (
          <Button fullWidth color="primary" onClick={() => {
            this.openJobModal(row, JOB_MODAL_ACTION.VIEW);}
          }>
            {row.id}
          </Button>
        ),
        sort: {
          enabled: false,
        },
      },
      {
        index: 1,
        title: 'Date created',
        name: 'dateCreated',
        sort: {
          enabled: false,
        },
      },
      {
        index: 2,
        title: 'Customer name',
        name: 'customerDetail_name',
        type: 'date',
        sort: {
          enabled: false,
        },
      },
      {
        index: 3,
        title: 'Email',
        name: 'customerDetail_email',
        sort: {
          enabled: false,
        },
      },
      {
        index: 4,
        title: 'Phone',
        name: 'customerDetail_phone',
        sort: {
          enabled: false,
        },
        cell: row => 
          (row.customerDetail.phoneNumber)
            ? <a className={classes.phoneLink} href={`tel:${row.customerDetail.phoneNumber}`}>{row.customerDetail.phoneNumber}</a>
            : <span className={classes.phoneNotProvided}>&ndash;</span>,
      },
      {
        index: 5,
        title: 'Lead Status',
        name: 'status',
        sort: {
          enabled: false,
        },
        cell: row => 
          <JobStatus 
            handleJobStatusChange={this.handleJobStatusChange} 
            jobId={row.rawId} 
            statusCode={row.statusCode} 
            status={row.status} 
          />,
      },
      {
        index: 6,
        title: '',
        name: 'actions',
        cell: row => (
          <div>
            <ActionsButton
              id={`jobs-list-${row.id}`}
              title={ACTIONS_BUTTON_TITLE}
              boundariesElement="viewport"
              actions={this.getJobActions(row)}
            />
            <ModalController>
              <JobModal modalIndex={row.id} />
            </ModalController>
          </div>
        ),
        sort: {
          enabled: false,
        },
      },
    ];

    if (isLoading) {
      return (
        <div className={classes.loadingWrapper}>
          <CircularProgress size={20} />
        </div>
      );
    }

    const tooltipText = 
      "You can now keep track of all of your jobs in one place. Click here to add a new job to your dashboard. Please note, you are not required to create a new job when using our Take-off tools. If you need assistance, please phone 13 11 03.";

    const tooltipStyle = {
      marginRight: '0.4rem',
    };

    return (
      <div className={classes.root}>
        <div className={classes.innerWrapper}>
          <div className={classes.tableWrapper}>
            <div className={classes.tableHeaderWrapper}>
              <Typography variant="h4" className={classes.tableTitle}>
                Manage jobs
              </Typography>
              <div>
                <Tooltip title={tooltipText} placement="top" style={tooltipStyle}>
                  <IconButton >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  className={classes.tableHeaderButton}
                  onClick={() => {
                    registerEventToGtm();
                    this.openJobModal(null, JOB_MODAL_ACTION.CREATE);}
                  }
                >
                  <Add className={classes.leftIcon} />
                  Create new job
                </Button>
              </div>
            </div>
            {width === 'xs' &&
              (<JobsContainerMobile
                jobs={this.formatJobs(jobs)}
                sort={sort}
                onSort={this.handleSort}
                getJobActions={this.getJobActions}
                openCreateModal={() => this.openJobModal(null, JOB_MODAL_ACTION.CREATE)}
                handleJobStatusChange={this.handleJobStatusChange}
              />)
            }
            {(width === 'sm' ||
              width === 'md' ||
              width === 'lg' ||
              width === 'xl') && (
              <JobsContainerDesktop
                jobs={this.formatJobs(jobs)}
                sort={sort}
                onSort={this.handleSort}
                columns={columns}
              />
            )}
          </div>
        </div>
        <ModalController>
          <JobModal modalIndex={CREATE_JOB_MODAL_INDEX}/>
        </ModalController>
      </div>
    );
  }
}

MyJobsContent.propTypes = propTypes;

const mapStateToProps = createStructuredSelector({
  isLoading: selectJobsLoading(),
  jobs: selectJobs(),
  jobsRetrieved: selectJobsRetrieved(),
});

const mapDispatchToProps = dispatch => ({
  onOpenModal: (displayName, modalProps) =>
    dispatch(openModal(displayName, modalProps)),
  onGetUserJobs: accessToken => dispatch(getJobs(accessToken)),
  // eslint-disable-next-line prettier/prettier
  onUpdateJob: (accessToken, jobId, payload) => dispatch(updateJob(accessToken, jobId, payload)),
});

export default withStyles(styles)(
  withWidth()(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(MyJobsContent),
  ),
);
